import {
    createSlice,
    createAsyncThunk,
} from '@reduxjs/toolkit'

/* =========================================================
   LOCAL STORAGE KEYS
========================================================= */

const CART_KEY = 'foodbite_cart'
const WISHLIST_KEY = 'foodbite_wishlist'


/* =========================================================
   LOCAL STORAGE HELPERS
========================================================= */

const getLocalCart = () => {
    try {
        const data = localStorage.getItem(CART_KEY)

        if (!data) {
            return []
        }

        const parsed = JSON.parse(data)

        return Array.isArray(parsed)
            ? parsed
            : []

    } catch (error) {
        console.log('getLocalCart error:', error)
        return []
    }
}


const getLocalWishlist = () => {
    try {
        const data = localStorage.getItem(WISHLIST_KEY)

        if (!data) {
            return []
        }

        const parsed = JSON.parse(data)

        return Array.isArray(parsed)
            ? parsed
            : []

    } catch (error) {
        console.log('getLocalWishlist error:', error)
        return []
    }
}


const saveLocalCart = (cart) => {
    try {
        localStorage.setItem(
            CART_KEY,
            JSON.stringify(cart)
        )
    } catch (error) {
        console.log('saveLocalCart error:', error)
    }
}


const saveLocalWishlist = (wishlist) => {
    try {
        localStorage.setItem(
            WISHLIST_KEY,
            JSON.stringify(wishlist)
        )
    } catch (error) {
        console.log('saveLocalWishlist error:', error)
    }
}


/* =========================================================
   REMOVE LOCAL STORAGE SAFELY
========================================================= */

const clearLocalCart = () => {
    try {
        localStorage.removeItem(CART_KEY)
    } catch (error) {
        console.log('clearLocalCart error:', error)
    }
}


const clearLocalWishlist = () => {
    try {
        localStorage.removeItem(WISHLIST_KEY)
    } catch (error) {
        console.log('clearLocalWishlist error:', error)
    }
}


/* =========================================================
   PRODUCT ID HELPER
========================================================= */

const getProductId = (item) => {
    if (!item) {
        return null
    }

    /*
        Supported structures:

        {
            productId,
            product,
            quantity
        }

        {
            _id
        }

        {
            id
        }

        {
            product: {
                _id
            }
        }

        {
            productInfo: {
                _id
            }
        }
    */

    if (typeof item !== 'object') {
        return item
    }

    return (
        item.productId ||
        item.product_id ||
        item._id ||
        item.id ||
        item.product?._id ||
        item.product?.productId ||
        item.product?.product_id ||
        item.product?.id ||
        item.productInfo?._id ||
        item.productInfo?.productId ||
        item.productInfo?.product_id ||
        item.productInfo?.id ||
        null
    )
}


/* =========================================================
   ACTION ID HELPER

   This makes delete/update work even if component sends:

   dispatch(delete_card_product(product._id))

   OR

   dispatch(delete_card_product(product))

   OR

   dispatch(delete_card_product({
       productId: product._id
   }))
========================================================= */

const getActionProductId = (payload) => {
    if (!payload) {
        return null
    }

    if (
        typeof payload === 'string' ||
        typeof payload === 'number'
    ) {
        return payload
    }

    return (
        payload.productId ||
        payload.product_id ||
        payload._id ||
        payload.id ||
        payload.card_id ||
        payload.product?._id ||
        payload.product?.productId ||
        payload.product?.product_id ||
        payload.product?.id ||
        getProductId(payload)
    )
}


/* =========================================================
   PRODUCT DATA HELPER
========================================================= */

const getProductData = (item) => {
    if (!item) {
        return null
    }

    if (item.product) {
        return item.product
    }

    if (item.productInfo) {
        return item.productInfo
    }

    return item
}


/* =========================================================
   PRICE HELPER
========================================================= */

const getProductPrice = (item) => {
    const product = getProductData(item)

    if (!product) {
        return 0
    }

    const price = Number(
        product.sellPrice ??
        product.sellingPrice ??
        product.price ??
        0
    )

    return Number.isFinite(price)
        ? price
        : 0
}


/* =========================================================
   QUANTITY HELPER
========================================================= */

const getQuantity = (item) => {
    const quantity = Number(item?.quantity)

    return Number.isFinite(quantity) && quantity > 0
        ? quantity
        : 1
}


/* =========================================================
   CART CALCULATION
========================================================= */

const calculateCart = (cart = []) => {
    let price = 0
    let buy_product_item = 0

    cart.forEach(item => {
        const quantity = getQuantity(item)
        const itemPrice = getProductPrice(item)

        price += itemPrice * quantity

        buy_product_item += quantity
    })

    return {
        price,
        buy_product_item,
    }
}


/* =========================================================
   CART STATE SYNC HELPER
========================================================= */

const syncCartState = (state) => {
    const cart = Array.isArray(state.localCart)
        ? state.localCart
        : []

    const calculation = calculateCart(cart)

    /*
        Main cart
    */

    state.card_products = cart

    /*
        Number of unique cart products
    */

    state.card_product_count = cart.length

    /*
        Total quantity
    */

    state.buy_product_item =
        calculation.buy_product_item

    /*
        Total price
    */

    state.price = calculation.price

    /*
        Keep local storage synced
    */

    saveLocalCart(cart)
}


/* =========================================================
   WISHLIST STATE SYNC HELPER
========================================================= */

const syncWishlistState = (state) => {
    const wishlist = Array.isArray(state.localWishlist)
        ? state.localWishlist
        : []

    state.localWishlist = wishlist
    state.wishlist = wishlist
    state.wishlist_count = wishlist.length

    saveLocalWishlist(wishlist)
}


/* =========================================================
   INITIAL DATA
========================================================= */

const initialLocalCart = getLocalCart()

const initialLocalWishlist =
    getLocalWishlist()

const initialCalculation =
    calculateCart(initialLocalCart)


/* =========================================================
   INITIAL STATE
========================================================= */

const initialState = {

    /* =========================
       CART
    ========================= */

    card_products:
        initialLocalCart,

    card_product_count:
        initialLocalCart.length,

    price:
        initialCalculation.price,

    buy_product_item:
        initialCalculation.buy_product_item,

    shipping_fee: 0,

    outofstock_products: [],


    /* =========================
       WISHLIST
    ========================= */

    wishlist:
        initialLocalWishlist,

    wishlist_count:
        initialLocalWishlist.length,


    /* =========================
       LOCAL CART
    ========================= */

    localCart:
        initialLocalCart,


    /* =========================
       LOCAL WISHLIST
    ========================= */

    localWishlist:
        initialLocalWishlist,


    /* =========================
       COMMON
    ========================= */

    loader: false,

    errorMessage: '',

    successMessage: '',
}


/* =========================================================
   ADD TO CART THUNK
========================================================= */

export const add_to_card = createAsyncThunk(
    'card/add_to_card',
    async (
        info,
        { rejectWithValue }
    ) => {
        try {
            return {
                local: true,
                info,
                message: 'Product added to cart',
            }

        } catch (error) {
            return rejectWithValue({
                error: 'Failed to add product to cart',
            })
        }
    }
)


/* =========================================================
   GET CART PRODUCTS
========================================================= */

export const get_card_products =
    createAsyncThunk(
        'card/get_card_products',
        async (
            _,
            { rejectWithValue }
        ) => {
            try {
                const cart = getLocalCart()

                const calculation =
                    calculateCart(cart)

                return {
                    local: true,

                    card_products:
                        cart,

                    price:
                        calculation.price,

                    card_product_count:
                        cart.length,

                    shipping_fee: 0,

                    outOfStockProduct: [],

                    buy_product_item:
                        calculation.buy_product_item,
                }

            } catch (error) {
                return rejectWithValue({
                    error: 'Failed to load cart',
                })
            }
        }
    )


/* =========================================================
   DELETE CART PRODUCT
========================================================= */

export const delete_card_product =
    createAsyncThunk(
        'card/delete_card_product',
        async (
            card_id,
            { rejectWithValue }
        ) => {
            try {

                const cart = getLocalCart()

                const productId =
                    getActionProductId(card_id)

                if (
                    productId === null ||
                    productId === undefined
                ) {
                    return rejectWithValue({
                        error:
                            'Product ID is missing',
                    })
                }

                const updatedCart =
                    cart.filter(item => {

                        const itemId =
                            getProductId(item)

                        return String(itemId) !==
                            String(productId)
                    })


                saveLocalCart(updatedCart)


                const calculation =
                    calculateCart(updatedCart)


                return {
                    local: true,

                    card_id:
                        productId,

                    cart:
                        updatedCart,

                    price:
                        calculation.price,

                    card_product_count:
                        updatedCart.length,

                    buy_product_item:
                        calculation.buy_product_item,

                    message:
                        'Product removed from cart',
                }

            } catch (error) {

                console.log(
                    'delete_card_product error:',
                    error
                )

                return rejectWithValue({
                    error:
                        'Failed to remove cart product',
                })
            }
        }
    )


/* =========================================================
   QUANTITY INCREASE
========================================================= */

export const quantity_inc =
    createAsyncThunk(
        'card/quantity_inc',
        async (
            card_id,
            { rejectWithValue }
        ) => {
            try {

                const cart = getLocalCart()

                const productId =
                    getActionProductId(card_id)

                const item =
                    cart.find(item =>
                        String(
                            getProductId(item)
                        ) ===
                        String(productId)
                    )


                if (!item) {
                    return rejectWithValue({
                        error:
                            'Cart product not found',
                    })
                }


                item.quantity =
                    getQuantity(item) + 1


                saveLocalCart(cart)


                const calculation =
                    calculateCart(cart)


                return {
                    local: true,

                    cart,

                    card_id:
                        productId,

                    price:
                        calculation.price,

                    card_product_count:
                        cart.length,

                    buy_product_item:
                        calculation.buy_product_item,

                    message:
                        'Quantity updated',
                }

            } catch (error) {

                return rejectWithValue({
                    error:
                        'Failed to increase quantity',
                })
            }
        }
    )


/* =========================================================
   QUANTITY DECREASE
========================================================= */

export const quantity_dec =
    createAsyncThunk(
        'card/quantity_dec',
        async (
            card_id,
            { rejectWithValue }
        ) => {
            try {

                const cart = getLocalCart()

                const productId =
                    getActionProductId(card_id)

                const item =
                    cart.find(item =>
                        String(
                            getProductId(item)
                        ) ===
                        String(productId)
                    )


                if (!item) {
                    return rejectWithValue({
                        error:
                            'Cart product not found',
                    })
                }


                const currentQuantity =
                    getQuantity(item)


                if (currentQuantity > 1) {
                    item.quantity =
                        currentQuantity - 1
                }


                saveLocalCart(cart)


                const calculation =
                    calculateCart(cart)


                return {
                    local: true,

                    cart,

                    card_id:
                        productId,

                    price:
                        calculation.price,

                    card_product_count:
                        cart.length,

                    buy_product_item:
                        calculation.buy_product_item,

                    message:
                        'Quantity updated',
                }

            } catch (error) {

                return rejectWithValue({
                    error:
                        'Failed to decrease quantity',
                })
            }
        }
    )


/* =========================================================
   GET WISHLIST
========================================================= */

export const get_wishlist_products =
    createAsyncThunk(
        'wishlist/get_wishlist_products',
        async (
            _,
            { rejectWithValue }
        ) => {
            try {

                const wishlist =
                    getLocalWishlist()

                return {
                    local: true,

                    wishlists:
                        wishlist,

                    wishlist:
                        wishlist,

                    products:
                        wishlist,

                    wishlistCount:
                        wishlist.length,

                    wishlist_count:
                        wishlist.length,
                }

            } catch (error) {

                return rejectWithValue({
                    error:
                        'Failed to load wishlist',
                })
            }
        }
    )


/* =========================================================
   ADD WISHLIST
========================================================= */

export const add_to_wishlist =
    createAsyncThunk(
        'wishlist/add_to_wishlist',
        async (
            info,
            { rejectWithValue }
        ) => {
            try {

                return {
                    local: true,

                    info,

                    message:
                        'Added to wishlist',
                }

            } catch (error) {

                return rejectWithValue({
                    error:
                        'Failed to add wishlist',
                })
            }
        }
    )


/* =========================================================
   REMOVE WISHLIST
========================================================= */

export const remove_wishlist =
    createAsyncThunk(
        'wishlist/remove_wishlist',
        async (
            wishlistId,
            { rejectWithValue }
        ) => {
            try {

                const wishlist =
                    getLocalWishlist()

                const productId =
                    getActionProductId(
                        wishlistId
                    )


                if (
                    productId === null ||
                    productId === undefined
                ) {
                    return rejectWithValue({
                        error:
                            'Wishlist product ID is missing',
                    })
                }


                const updatedWishlist =
                    wishlist.filter(item =>
                        String(
                            getProductId(item)
                        ) !==
                        String(productId)
                    )


                saveLocalWishlist(
                    updatedWishlist
                )


                return {
                    local: true,

                    wishlistId:
                        productId,

                    wishlist:
                        updatedWishlist,

                    message:
                        'Removed from wishlist',
                }

            } catch (error) {

                return rejectWithValue({
                    error:
                        'Failed to remove wishlist',
                })
            }
        }
    )


/* =========================================================
   CARD REDUCER
========================================================= */

export const cardReducer = createSlice({

    name: 'card',

    initialState,

    reducers: {

        /* =================================================
           ADD GUEST CART
        ================================================= */

        add_guest_cart: (
            state,
            action
        ) => {

            const payload =
                action.payload || {}


            const product =
                getProductData(payload)


            const productId =
                payload?.productId ||
                payload?.product_id ||
                getProductId(product)


            const quantity =
                Math.max(
                    1,
                    Number(
                        payload?.quantity
                    ) || 1
                )


            if (!productId) {

                state.errorMessage =
                    'Product information is missing'

                return
            }


            const existing =
                state.localCart.find(item =>
                    String(
                        getProductId(item)
                    ) ===
                    String(productId)
                )


            if (existing) {

                existing.quantity =
                    getQuantity(existing) +
                    quantity


                /*
                   Update latest product data
                */

                if (payload?.product) {
                    existing.product =
                        payload.product
                }

            } else {

                state.localCart.push({
                    productId,
                    quantity,
                    product,
                })
            }


            syncCartState(state)


            state.successMessage =
                'Product added to cart'

            state.errorMessage = ''
        },


        /* =================================================
           UPDATE GUEST CART
        ================================================= */

        update_guest_cart: (
            state,
            action
        ) => {

            const payload =
                action.payload || {}


            const productId =
                getActionProductId(payload)


            const quantity =
                Math.max(
                    1,
                    Number(
                        payload.quantity
                    ) || 1
                )


            const item =
                state.localCart.find(item =>
                    String(
                        getProductId(item)
                    ) ===
                    String(productId)
                )


            if (!item) {
                return
            }


            item.quantity =
                quantity


            syncCartState(state)
        },


        /* =================================================
           REMOVE GUEST CART
        ================================================= */

        remove_guest_cart: (
            state,
            action
        ) => {

            const productId =
                getActionProductId(
                    action.payload
                )


            if (
                productId === null ||
                productId === undefined
            ) {
                state.errorMessage =
                    'Product ID is missing'

                return
            }


            state.localCart =
                state.localCart.filter(item =>
                    String(
                        getProductId(item)
                    ) !==
                    String(productId)
                )


            /*
               IMPORTANT:
               Sync everything after delete
            */

            syncCartState(state)


            state.successMessage =
                'Product removed from cart'

            state.errorMessage = ''
        },


        /* =================================================
           CLEAR GUEST CART
        ================================================= */

        clear_guest_cart: (
            state
        ) => {

            state.localCart = []

            state.card_products = []

            state.card_product_count = 0

            state.price = 0

            state.buy_product_item = 0

            state.shipping_fee = 0

            state.outofstock_products = []


            clearLocalCart()


            state.successMessage =
                'Cart cleared'
        },


        /* =================================================
           ADD GUEST WISHLIST
        ================================================= */

        add_guest_wishlist: (
            state,
            action
        ) => {

            const payload =
                action.payload || {}


            const product =
                getProductData(payload)


            const productId =
                payload?.productId ||
                payload?.product_id ||
                getProductId(product)


            if (!productId) {

                state.errorMessage =
                    'Product information is missing'

                return
            }


            const exists =
                state.localWishlist.some(item =>
                    String(
                        getProductId(item)
                    ) ===
                    String(productId)
                )


            if (!exists) {

                state.localWishlist.push(
                    product
                )

                state.successMessage =
                    'Added to wishlist'

            } else {

                state.successMessage =
                    'Product already in wishlist'
            }


            state.wishlist =
                state.localWishlist

            state.wishlist_count =
                state.localWishlist.length


            saveLocalWishlist(
                state.localWishlist
            )


            state.errorMessage = ''
        },


        /* =================================================
           REMOVE GUEST WISHLIST
        ================================================= */

        remove_guest_wishlist: (
            state,
            action
        ) => {

            const productId =
                getActionProductId(
                    action.payload
                )


            if (
                productId === null ||
                productId === undefined
            ) {
                state.errorMessage =
                    'Product ID is missing'

                return
            }


            state.localWishlist =
                state.localWishlist.filter(item =>
                    String(
                        getProductId(item)
                    ) !==
                    String(productId)
                )


            state.wishlist =
                state.localWishlist

            state.wishlist_count =
                state.localWishlist.length


            saveLocalWishlist(
                state.localWishlist
            )


            state.successMessage =
                'Removed from wishlist'

            state.errorMessage = ''
        },


        /* =================================================
           CLEAR GUEST WISHLIST
        ================================================= */

        clear_guest_wishlist: (
            state
        ) => {

            state.localWishlist = []

            state.wishlist = []

            state.wishlist_count = 0


            clearLocalWishlist()


            state.successMessage =
                'Wishlist cleared'
        },


        /* =================================================
           MESSAGE CLEAR
        ================================================= */

        messageClear: (
            state
        ) => {

            state.errorMessage = ''

            state.successMessage = ''
        },


        /* =================================================
           RESET COUNT
        ================================================= */

        reset_count: (
            state
        ) => {

            state.card_product_count =
                state.localCart.length

            state.wishlist_count =
                state.localWishlist.length

            /*
               Also recalculate total quantity
            */

            const calculation =
                calculateCart(
                    state.localCart
                )

            state.buy_product_item =
                calculation.buy_product_item
        },


        /* =================================================
           CARD RESET
        ================================================= */

        card_reset: (
            state
        ) => {

            state.card_products = []

            state.wishlist = []

            state.price = 0

            state.buy_product_item = 0

            state.shipping_fee = 0

            state.outofstock_products = []

            state.localCart = []

            state.localWishlist = []

            state.card_product_count = 0

            state.wishlist_count = 0


            clearLocalCart()

            clearLocalWishlist()


            state.successMessage =
                'Cart reset successfully'
        },
    },


    /* =====================================================
       EXTRA REDUCERS
    ===================================================== */

    extraReducers: (builder) => {

        /* =================================================
           ADD TO CART
        ================================================= */

        builder.addCase(
            add_to_card.pending,
            (state) => {

                state.loader = true

                state.errorMessage = ''
            }
        )


        builder.addCase(
            add_to_card.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to add product to cart'
            }
        )


        builder.addCase(
            add_to_card.fulfilled,
            (state, { payload }) => {

                state.loader = false


                const info =
                    payload?.info


                if (info) {

                    const product =
                        getProductData(info)


                    const productId =
                        info?.productId ||
                        info?.product_id ||
                        getProductId(product)


                    const quantity =
                        Math.max(
                            1,
                            Number(
                                info?.quantity
                            ) || 1
                        )


                    if (productId) {

                        const existing =
                            state.localCart.find(
                                item =>
                                    String(
                                        getProductId(item)
                                    ) ===
                                    String(productId)
                            )


                        if (existing) {

                            existing.quantity =
                                getQuantity(existing) +
                                quantity


                            if (info.product) {
                                existing.product =
                                    info.product
                            }

                        } else {

                            state.localCart.push({
                                productId,
                                quantity,
                                product,
                            })
                        }
                    }
                }


                /*
                   ALWAYS SYNC CART
                */

                syncCartState(state)


                state.successMessage =
                    payload?.message ||
                    'Product added to cart'
            }
        )


        /* =================================================
           GET CART
        ================================================= */

        builder.addCase(
            get_card_products.pending,
            (state) => {

                state.loader = true
            }
        )


        builder.addCase(
            get_card_products.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to load cart'
            }
        )


        builder.addCase(
            get_card_products.fulfilled,
            (state, { payload }) => {

                state.loader = false


                state.localCart =
                    Array.isArray(
                        payload?.card_products
                    )
                        ? payload.card_products
                        : getLocalCart()


                /*
                   Recalculate from actual cart
                   instead of trusting old values
                */

                const calculation =
                    calculateCart(
                        state.localCart
                    )


                state.card_products =
                    state.localCart

                state.card_product_count =
                    state.localCart.length

                state.price =
                    calculation.price

                state.buy_product_item =
                    calculation.buy_product_item

                state.shipping_fee =
                    payload?.shipping_fee || 0

                state.outofstock_products =
                    payload?.outOfStockProduct || []


                saveLocalCart(
                    state.localCart
                )
            }
        )


        /* =================================================
           DELETE CART
        ================================================= */

        builder.addCase(
            delete_card_product.pending,
            (state) => {

                state.loader = true

                state.errorMessage = ''
            }
        )


        builder.addCase(
            delete_card_product.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to remove cart product'
            }
        )


        builder.addCase(
            delete_card_product.fulfilled,
            (state, { payload }) => {

                state.loader = false


                /*
                   IMPORTANT:
                   Take the NEW cart
                */

                if (
                    Array.isArray(
                        payload?.cart
                    )
                ) {

                    state.localCart =
                        payload.cart
                }


                /*
                   IMPORTANT:
                   Sync ALL cart values
                */

                const calculation =
                    calculateCart(
                        state.localCart
                    )


                state.card_products =
                    state.localCart

                state.card_product_count =
                    state.localCart.length

                state.price =
                    calculation.price

                state.buy_product_item =
                    calculation.buy_product_item


                state.successMessage =
                    payload?.message ||
                    'Product removed from cart'

                state.errorMessage = ''


                /*
                   Make sure localStorage
                   is also updated
                */

                saveLocalCart(
                    state.localCart
                )
            }
        )


        /* =================================================
           QUANTITY INC
        ================================================= */

        builder.addCase(
            quantity_inc.pending,
            (state) => {

                state.loader = true
            }
        )


        builder.addCase(
            quantity_inc.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to increase quantity'
            }
        )


        builder.addCase(
            quantity_inc.fulfilled,
            (state, { payload }) => {

                state.loader = false


                if (
                    Array.isArray(
                        payload?.cart
                    )
                ) {

                    state.localCart =
                        payload.cart
                }


                syncCartState(state)


                state.successMessage =
                    payload?.message ||
                    'Quantity updated'
            }
        )


        /* =================================================
           QUANTITY DEC
        ================================================= */

        builder.addCase(
            quantity_dec.pending,
            (state) => {

                state.loader = true
            }
        )


        builder.addCase(
            quantity_dec.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to decrease quantity'
            }
        )


        builder.addCase(
            quantity_dec.fulfilled,
            (state, { payload }) => {

                state.loader = false


                if (
                    Array.isArray(
                        payload?.cart
                    )
                ) {

                    state.localCart =
                        payload.cart
                }


                syncCartState(state)


                state.successMessage =
                    payload?.message ||
                    'Quantity updated'
            }
        )


        /* =================================================
           ADD WISHLIST
        ================================================= */

        builder.addCase(
            add_to_wishlist.pending,
            (state) => {

                state.loader = true

                state.errorMessage = ''
            }
        )


        builder.addCase(
            add_to_wishlist.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to add wishlist'
            }
        )


        builder.addCase(
            add_to_wishlist.fulfilled,
            (state, { payload }) => {

                state.loader = false


                const info =
                    payload?.info


                if (info) {

                    const product =
                        getProductData(info)


                    const productId =
                        info?.productId ||
                        info?.product_id ||
                        getProductId(product)


                    if (productId) {

                        const exists =
                            state.localWishlist.some(
                                item =>
                                    String(
                                        getProductId(item)
                                    ) ===
                                    String(productId)
                            )


                        if (!exists) {

                            state.localWishlist.push(
                                product
                            )
                        }
                    }
                }


                syncWishlistState(state)


                state.successMessage =
                    payload?.message ||
                    'Added to wishlist'
            }
        )


        /* =================================================
           GET WISHLIST
        ================================================= */

        builder.addCase(
            get_wishlist_products.pending,
            (state) => {

                state.loader = true
            }
        )


        builder.addCase(
            get_wishlist_products.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to load wishlist'
            }
        )


        builder.addCase(
            get_wishlist_products.fulfilled,
            (state, { payload }) => {

                state.loader = false


                state.localWishlist =
                    Array.isArray(
                        payload?.wishlists
                    )
                        ? payload.wishlists
                        : Array.isArray(
                            payload?.wishlist
                        )
                            ? payload.wishlist
                            : Array.isArray(
                                payload?.products
                            )
                                ? payload.products
                                : getLocalWishlist()


                state.wishlist =
                    state.localWishlist

                state.wishlist_count =
                    state.localWishlist.length


                saveLocalWishlist(
                    state.localWishlist
                )
            }
        )


        /* =================================================
           REMOVE WISHLIST
        ================================================= */

        builder.addCase(
            remove_wishlist.pending,
            (state) => {

                state.loader = true

                state.errorMessage = ''
            }
        )


        builder.addCase(
            remove_wishlist.rejected,
            (state, { payload }) => {

                state.loader = false

                state.errorMessage =
                    payload?.error ||
                    payload?.message ||
                    'Failed to remove wishlist'
            }
        )


        builder.addCase(
            remove_wishlist.fulfilled,
            (state, { payload }) => {

                state.loader = false


                if (
                    Array.isArray(
                        payload?.wishlist
                    )
                ) {

                    state.localWishlist =
                        payload.wishlist
                }


                state.wishlist =
                    state.localWishlist

                state.wishlist_count =
                    state.localWishlist.length


                saveLocalWishlist(
                    state.localWishlist
                )


                state.successMessage =
                    payload?.message ||
                    'Removed from wishlist'

                state.errorMessage = ''
            }
        )
    },
})


/* =========================================================
   ACTIONS
========================================================= */

export const {
    add_guest_cart,
    update_guest_cart,
    remove_guest_cart,
    clear_guest_cart,

    add_guest_wishlist,
    remove_guest_wishlist,
    clear_guest_wishlist,

    messageClear,
    reset_count,
    card_reset,

} = cardReducer.actions


/* =========================================================
   EXPORT
========================================================= */

export default cardReducer.reducer